import React from 'react';
import { CheckCircle, FileText } from 'lucide-react';
import ExportModal from '../../../shared/components/ExportModal';
import PPTGenerationModal from '../../../../ppt/components/PPTGenerationModal';
import VersionDetailsModal from './modals/VersionDetailsModal';
import VersionComparisonModal from './modals/VersionComparisonModal';
import VersionHistorySidebar from '../../../shared/components/VersionHistorySidebar';
import CustomLoader from '../../../../../shared/components/ui/CustomLoader';
import SuccessToast from '../../../../../shared/components/ui/SuccessToast';

/**
 * RFPOverlays — extracted from RFPAnalysisResult.jsx (Phase 6 refactor).
 * Renders all floating/overlay elements:
 *   - Floating status popup (save/update feedback)
 *   - Alert modal (stage transition feedback)
 *   - Export modal
 *   - PPT Generation modal
 *   - Version Details modal
 *   - Version Comparison modal
 *   - Section tab tooltip
 *   - Version History sidebar
 *   - Generating slides overlay
 * Zero logic changes. All state/handlers passed as props from parent.
 */
const RFPOverlays = ({
    // Status & Alert
    updateStatus,
    alertModal,
    setAlertModal,
    onConfirm,
    navigate,
    // Export & PPT
    showExport,
    setShowExport,
    showPPTModal,
    setShowPPTModal,
    rfpId,
    editableData,
    // Version modals
    showVersionDetailsModal,
    setShowVersionDetailsModal,
    selectedVersionData,
    activeSection,
    versionsList,
    setRevertTargetVersion,
    setShowRevertModal,
    fetchComparison,
    handleRenameVersion,
    showComparisonModal,
    setShowComparisonModal,
    comparisonData,
    // Tooltip
    hoveredTabDetails,
    stripPrefix,
    isManager,
    // Version History sidebar
    showVersionHistory,
    setShowVersionHistory,
    activeVersionMenuId,
    setActiveVersionMenuId,
    handleViewVersion,
    isFetchingVersionDetails,
    setLabelTargetVersion,
    setVersionLabelText,
    setShowLabelModal,
    setIsSelectingCompareFromView,
    isSelectingCompareFromView,
    compareSourceVersion,
    setCompareSourceVersion,
    setSelectedVersionData,
    isFetchingComparison,
    handleApproveVersion,
    isApprovingVersion,
    approvingVersionNum,
    // Generating slides
    isGeneratingSlides,
    // Modal check
    isAnyModalOpen
}) => {
    const activeSectionObj = (editableData?.sections || []).find(s =>
        (s.label && s.label === activeSection) ||
        (s.key && s.key === activeSection) ||
        (s.section_title && s.section_title === activeSection) ||
        (s.section_key && s.section_key === activeSection) ||
        (s.id && String(s.id) === String(activeSection)) ||
        (s.section_id && String(s.section_id) === String(activeSection))
    );
    return (
        <>
            {/* Floating save/update status */}
            {updateStatus && (
                <SuccessToast toast={updateStatus} />
            )}

            {/* Stage transition alert */}
            {alertModal.show && (() => {
                const rawMsg = alertModal.message;
                const mStr = (() => {
                    if (!rawMsg) return '';
                    if (typeof rawMsg === 'string') return rawMsg;
                    if (typeof rawMsg === 'object') return rawMsg.body || rawMsg.content || rawMsg.text || rawMsg.message || rawMsg.title || JSON.stringify(rawMsg);
                    return String(rawMsg);
                })().toLowerCase();

                // Suppress assignment alerts in favor of toasts
                if (mStr.includes('assigned') || mStr.includes('reassigned') || (mStr.includes('section:') && mStr.includes('assigned to'))) {
                    return null;
                }

                const isProposalWon = mStr.includes('accepted') || mStr.includes('won');

                if (isProposalWon) {
                    const clientName = editableData?.CLIENT_NAME || editableData?.client_name || editableData?.client_place || 'Client';
                    const proposalTitle = editableData?.PROPOSAL_TITLE || editableData?.proposal_title || 'Untitled Proposal';
                    const contractValue = editableData?.VALUATION || editableData?.valuation || editableData?.estimated_budget || 'N/A';

                    const n = alertModal.notes || '';
                    const nStr = typeof n === 'string' ? n : (n.body || n.content || n.text || n.message || n.title || JSON.stringify(n));

                    return (
                        <div className="status-modal-overlay" style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(6px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
                            animation: 'modalFadeIn 200ms ease-out'
                        }}>
                            <style>{`
                                @keyframes modalFadeIn { from { opacity: 0; } to { opacity: 1; } }
                                @keyframes modalScaleIn { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                                @keyframes iconPulseGlow { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); transform: scale(0.95); } 50% { box-shadow: 0 0 30px 15px rgba(16, 185, 129, 0.15); transform: scale(1.05); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); transform: scale(1); } }
                            `}</style>
                            <div style={{
                                background: '#FFFFFF',
                                width: '100%',
                                maxWidth: '500px',
                                borderRadius: '8px',
                                padding: '1rem',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                animation: 'modalScaleIn 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                                fontFamily: "'Inter', system-ui, sans-serif"
                            }}>
                                {/* Top Section */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>                                    <div style={{
                                        width: '54px', height: '54px', borderRadius: '50%', background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        animation: 'iconPulseGlow 2s ease-out', color: '#15803D', boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.5)'
                                    }}>
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                                            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                                            <path d="M4 22h16"></path>
                                            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                                            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                                            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                                        </svg>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.02em' }}>Proposal Won</h2>
                                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748B', fontWeight: '500' }}>The client has officially accepted this proposal.</p>
                                    </div>
                                </div>

                                {/* Information Cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                    <div style={{ background: '#F8F9FA', padding: '1rem', borderRadius: '10px', border: '1px solid #F1F5F9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Client</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1E293B', lineHeight: '1.4' }}>{clientName}</div>
                                    </div>
                                    <div style={{ background: '#F8F9FA', padding: '1rem', borderRadius: '10px', border: '1px solid #F1F5F9', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Proposal</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1E293B', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{proposalTitle}</div>
                                    </div>
                                </div>

                                {/* Client Feedback */}
                                {nStr.trim() && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
                                        <div style={{
                                            background: '#F8F9FA',
                                            padding: '1rem 1.25rem',
                                            borderRadius: '0 10px 10px 0',
                                            position: 'relative',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                        }}>
                                            <div style={{ fontSize: '0.95rem', fontStyle: 'italic', color: '#334155', lineHeight: '1.5', fontWeight: '500' }}>"{nStr}"</div>
                                        </div>
                                    </div>
                                )}

                                {/* Business Impact */}
                                <div style={{
                                    background: 'linear-gradient(to right, #F0FDF4, #DCFCE7)',
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    border: '1px solid #BBF7D0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.05)'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.9 }}>Contract Value</div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#14532D', letterSpacing: '-0.02em' }}>{contractValue}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#15803D', fontWeight: '700', fontSize: '0.75rem', background: 'rgba(255,255,255,0.6)', padding: '0.35rem 0.65rem', borderRadius: '6px' }}>
                                        <CheckCircle size={14} strokeWidth={3} />
                                        Awarded
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '0.25rem' }}>
                                    <button onClick={() => {
                                        if (alertModal.isConfirmation && onConfirm) onConfirm();
                                        else setAlertModal({ show: false, message: '' });
                                    }} style={{
                                        flex: 1, padding: '0.65rem', background: '#FF5526', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 14px 0 rgba(255, 85, 38,0.25)'
                                    }} onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                        OK
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }

                    // Default Generic Alert
                    return (
                            <div className="status-modal-overlay">
                                <div className="status-modal-content">
                                    <div className="status-modal-icon">
                                        <CheckCircle size={32} color="#10b981" />
                                    </div>
                                    <p>{(() => {
                                        const m = alertModal.message;
                                        if (!m) return '';
                                        if (typeof m === 'string') return m;
                                        if (typeof m === 'object') return m.body || m.content || m.text || m.message || m.title || JSON.stringify(m);
                                        return String(m);
                                    })()}</p>
                                    {alertModal.notes && (() => {
                                        const m = alertModal.message || '';
                                        const n = alertModal.notes || '';
                                        const mStrCheck = typeof m === 'string' ? m : (m.body || m.content || m.text || m.message || m.title || JSON.stringify(m));
                                        const nStr = typeof n === 'string' ? n : (n.body || n.content || n.text || n.message || n.title || JSON.stringify(n));

                                        if (!nStr.trim()) return null;
                                        if (mStrCheck.toLowerCase().trim() === nStr.toLowerCase().trim()) return null;

                                        return (
                                            <div style={{
                                                marginTop: '10px',
                                                marginBottom: '10px',
                                                padding: '12px 14px',
                                                background: '#F8F9FA',
                                                borderLeft: '4px solid #FF5526',
                                                borderRadius: '8px',
                                                fontSize: '0.85rem',
                                                color: '#475569',
                                                textAlign: 'left',
                                                width: '100%',
                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                                                lineHeight: '1.4'
                                            }}>
                                                <div style={{ fontWeight: '700', color: '#1E293B', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Note / Message:</div>
                                                <div style={{ fontStyle: 'italic', color: '#64748B' }}>"{nStr}"</div>
                                            </div>
                                        );
                                    })()}
                                    <div className="status-modal-actions" style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '0.5rem', justifyContent: 'center' }}>
                                        {alertModal.isConfirmation && (
                                            <button className="status-modal-cancel" onClick={() => {
                                                setAlertModal({ show: false, message: '' });
                                            }} style={{
                                                flex: 1,
                                                background: '#F1F5F9',
                                                color: '#475569',
                                                border: 'none',
                                                padding: '0.6rem 1rem',
                                                borderRadius: '10px',
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}>
                                                Cancel
                                            </button>
                                        )}
                                        <button className="status-modal-close" onClick={() => {
                                            if (alertModal.isConfirmation && onConfirm) {
                                                onConfirm();
                                            } else {
                                                setAlertModal({ show: false, message: '' });
                                            }
                                        }} style={{
                                            flex: alertModal.isConfirmation ? 1 : 0,
                                            minWidth: alertModal.isConfirmation ? 'auto' : '120px',
                                            padding: '0.6rem 1.5rem',
                                            textAlign: 'center'
                                        }}>
                                            {alertModal.isConfirmation ? 'Continue' : 'OK'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            );
            })()}

                            {/* Export Modal */}
                            {showExport && (
                                <ExportModal
                                    rfpId={rfpId}
                                    analysisData={editableData}
                                    isManager={isManager}
                                    onClose={() => setShowExport(false)}
                                    onPPTClick={() => {
                                        setShowExport(false);
                                        setShowPPTModal(true);
                                    }}
                                />
                            )}

                            {/* PPT Generation Modal */}
                            <PPTGenerationModal
                                isOpen={showPPTModal}
                                onClose={() => setShowPPTModal(false)}
                                rfpId={rfpId}
                                navigate={navigate}
                                editableData={editableData}
                            />

                            {/* Version Details Modal */}
                            {showVersionDetailsModal && (
                                <VersionDetailsModal
                                    isOpen={showVersionDetailsModal}
                                    onClose={() => setShowVersionDetailsModal(false)}
                                    versionData={selectedVersionData}
                                    activeSection={activeSection}
                                    versionsList={versionsList}
                                    onRevert={(v) => {
                                        setRevertTargetVersion(v);
                                        setShowRevertModal(true);
                                    }}
                                    onCompare={fetchComparison}
                                    onRename={handleRenameVersion}
                                    onApprove={async (vNum) => {
                                        if (handleApproveVersion) {
                                            const res = await handleApproveVersion(vNum);
                                            if (res?.success) setShowVersionDetailsModal(false);
                                        }
                                    }}
                                    isManager={isManager}
                                    currentSection={activeSectionObj}
                                    isApproving={isApprovingVersion}
                                />
                            )}

                            {/* Version Comparison Modal */}
                            {showComparisonModal && (
                                <VersionComparisonModal
                                    isOpen={showComparisonModal}
                                    onClose={() => setShowComparisonModal(false)}
                                    comparisonData={comparisonData}
                                    activeSection={activeSection}
                                    versionsList={versionsList}
                                />
                            )}

                            {/* Section tab hover tooltip */}
                            {hoveredTabDetails && !isAnyModalOpen && (() => {
                                const hasNotes = hoveredTabDetails.notes && String(hoveredTabDetails.notes).trim() !== '';
                                if (!isManager && !hasNotes) return null;

                                return (
                                    <div
                                        className="section-tab-tooltip-floating"
                                        style={{
                                            position: 'fixed',
                                            top: `${hoveredTabDetails.top}px`,
                                            left: `${hoveredTabDetails.left}px`,
                                            zIndex: 9999,
                                            background: !isManager ? '#FFFBEB' : '#1e293b',
                                            border: !isManager ? '1px solid #FDE68A' : 'none',
                                            color: !isManager ? '#78350F' : '#ffffff',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            maxWidth: '280px',
                                            fontSize: '0.75rem',
                                            lineHeight: '1.4'
                                        }}
                                    >
                                        {!isManager ? (
                                            <div>
                                                <div style={{ fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px', color: '#B45309' }}>
                                                    📝 Section Notes:
                                                </div>
                                                <div style={{ fontStyle: 'italic', wordBreak: 'break-word' }}>{hoveredTabDetails.notes}</div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="tooltip-section-name">Section: {stripPrefix(hoveredTabDetails.key)}</div>
                                                <div className="tooltip-assigned-info">
                                                    {hoveredTabDetails.isAssigned ? `Assigned to: ${hoveredTabDetails.assignedMembers.join(', ')}` : 'Status: Unassigned'}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Version History Sidebar */}
                            {showVersionHistory && (
                                <VersionHistorySidebar
                                    showVersionSidebar={showVersionHistory}
                                    setShowVersionSidebar={setShowVersionHistory}
                                    versionsList={versionsList}
                                    activeSection={activeSection}
                                    stripPrefix={stripPrefix}
                                    activeVersionMenuId={activeVersionMenuId}
                                    setActiveVersionMenuId={setActiveVersionMenuId}
                                    handleViewVersion={handleViewVersion}
                                    isFetchingVersionDetails={isFetchingVersionDetails}
                                    setLabelTargetVersion={setLabelTargetVersion}
                                    setVersionLabelText={setVersionLabelText}
                                    setShowLabelModal={setShowLabelModal}
                                    setRevertTargetVersion={setRevertTargetVersion}
                                    setShowRevertModal={setShowRevertModal}
                                    setIsSelectingCompareFromView={setIsSelectingCompareFromView}
                                    isSelectingCompareFromView={isSelectingCompareFromView}
                                    compareSourceVersion={compareSourceVersion}
                                    setCompareSourceVersion={setCompareSourceVersion}
                                    fetchComparison={fetchComparison}
                                    setSelectedVersionData={setSelectedVersionData}
                                    setShowVersionDetailsModal={setShowVersionDetailsModal}
                                    isFetchingComparison={isFetchingComparison}
                                    isManager={isManager}
                                    currentSection={activeSectionObj}
                                    handleApproveVersion={handleApproveVersion}
                                    isApprovingVersion={isApprovingVersion}
                                    approvingVersionNum={approvingVersionNum}
                                />
                            )}

                            {/* Generating slides full-screen overlay */}
                            {isGeneratingSlides && (
                                <div className="custom-modal-overlay" style={{ zIndex: 10000 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                        <CustomLoader size={48} />
                                        <p style={{ color: 'white', fontWeight: '600', fontSize: '1.2rem' }}>Generating Proposal Slides...</p>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                };

                export default RFPOverlays;
